import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, fmtNum, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Search, Package, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";
import ConfirmSoftDelete from "../components/ConfirmSoftDelete";

const empty = { sku: "", name: "", category: "", unit: "adet", min_stock: 0, max_stock: 0, current_stock: 0, last_price: 0, is_service: false };

export default function Products() {
    const { user } = useAuth();
    const canWrite = can(user, "masterdata.write");
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try { const r = await api.get("/products"); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU ve ad zorunludur"); return; }
        setBusy(true);
        try {
            const p = { ...form,
                min_stock: Number(form.min_stock), max_stock: Number(form.max_stock),
                current_stock: Number(form.current_stock), last_price: Number(form.last_price),
            };
            await api.post("/products", p);
            toast.success("Ürün oluşturuldu");
            setOpen(false); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/products/${toDelete.id}`);
            toast.success("Ürün geri dönüşüm kutusuna taşındı");
            setToDelete(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };

    const filtered = items.filter((p) =>
        !q || p.name?.toLowerCase().includes(q.toLowerCase()) || p.sku?.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <AppShell
            title="Ürünler & Hizmetler"
            subtitle="Ana Veri"
            actions={
                canWrite ? (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-product-btn" className="gap-2"><Plus size={16}/> Yeni Ürün</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Yeni Ürün</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-4">
                            <F label="SKU *"><Input data-testid="prod-sku" value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})}/></F>
                            <F label="Adı *"><Input data-testid="prod-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/></F>
                            <F label="Kategori"><Input value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}/></F>
                            <F label="Birim"><Input value={form.unit} onChange={(e)=>setForm({...form, unit:e.target.value})}/></F>
                            <F label="Min. Stok"><Input type="number" value={form.min_stock} onChange={(e)=>setForm({...form, min_stock:e.target.value})}/></F>
                            <F label="Max. Stok"><Input type="number" value={form.max_stock} onChange={(e)=>setForm({...form, max_stock:e.target.value})}/></F>
                            <F label="Mevcut Stok"><Input type="number" value={form.current_stock} onChange={(e)=>setForm({...form, current_stock:e.target.value})}/></F>
                            <F label="Son Fiyat"><Input type="number" value={form.last_price} onChange={(e)=>setForm({...form, last_price:e.target.value})}/></F>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={()=>setOpen(false)}>İptal</Button>
                            <Button data-testid="save-product" onClick={submit} disabled={busy}>
                                {busy && <Loader2 size={14} className="mr-2 animate-spin"/>}
                                Kaydet
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                ) : null
            }
        >
            <div className="bg-card border border-border rounded-md">
                <div className="p-4 border-b border-border flex items-center gap-3">
                    <div className="relative w-72">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                        <Input data-testid="prod-search" placeholder="Ara..." value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9"/>
                    </div>
                    <div className="text-xs text-muted-foreground ml-auto">{filtered.length} ürün</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>SKU</th><th>Ad</th><th>Kategori</th><th>Birim</th>
                                <th className="text-right">Mevcut</th>
                                <th className="text-right">Min</th>
                                <th className="text-right">Max</th>
                                <th className="text-right">Son Fiyat</th>
                                <th className="text-right">Ort. Maliyet</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} className="text-center py-16 text-muted-foreground">
                                    <Loader2 size={22} className="mx-auto mb-2 animate-spin opacity-70"/>
                                    Yükleniyor...
                                </td></tr>
                            ) : filtered.map((p)=>(
                                <tr key={p.id} data-testid={`prod-row-${p.sku}`}>
                                    <td className="font-mono text-xs">{p.sku}</td>
                                    <td className="font-medium">{p.name}</td>
                                    <td>{p.category || "—"}</td>
                                    <td>{p.unit}</td>
                                    <td className={`text-right tabular-nums ${p.current_stock < p.min_stock ? "text-rose-600 font-medium" : ""}`}>
                                        <div className="flex items-center justify-end gap-1">
                                            {p.current_stock < p.min_stock && <AlertTriangle size={12}/>}
                                            {fmtNum(p.current_stock, 0)}
                                        </div>
                                    </td>
                                    <td className="text-right tabular-nums text-muted-foreground">{fmtNum(p.min_stock, 0)}</td>
                                    <td className="text-right tabular-nums text-muted-foreground">{fmtNum(p.max_stock, 0)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(p.last_price, p.currency)}</td>
                                    <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(p.avg_cost, p.currency)}</td>
                                    <td className="text-right">
                                        {canWrite && (
                                            <button
                                                data-testid={`prod-delete-${p.sku}`}
                                                onClick={() => setToDelete(p)}
                                                className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-950/20 text-muted-foreground hover:text-rose-600"
                                                title="Geri dönüşüm kutusuna taşı"
                                            >
                                                <Trash2 size={14}/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!loading && filtered.length===0 && (
                                <tr><td colSpan={10} className="text-center py-16 text-muted-foreground">
                                    <Package size={28} className="mx-auto mb-2 opacity-40"/>
                                    Ürün bulunamadı
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <ConfirmSoftDelete
                open={!!toDelete}
                onOpenChange={(o)=>!o && setToDelete(null)}
                onConfirm={confirmDelete}
                busy={deleting}
                itemLabel={toDelete ? `${toDelete.sku} — ${toDelete.name}` : null}
                testid="confirm-delete-product"
            />
        </AppShell>
    );
}

function F({label,children}){return<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>}
